import {
  saveScheduledActions,
  PeriodType,
  getActionMethodFromOption,
  ActionMethod,
  encodeCronSchedule
} from 'src/store/modules/adManager/adScheduledActionGroups/selectors';
import { AdCampaignManagerServiceClient } from 'src/utils/adCampaignManagerServiceClient/adCampaignManagerServiceClient';

import _cloneDeep from 'lodash/cloneDeep';
import { RuleStoreProps } from './stores';
import axios from 'axios';
import { getCampaignDisplayStatus } from 'src/components/AdManager/AmsUtils';
import _uniq from 'lodash/uniq';
import { getTimezoneOffset, timeZoneToDateTime, generateDayIdLabel, TimeZone } from './RuleTimes/utils';
import { AD_CAMPAIGN_STATUS, AD_STATUS_DERIVED } from 'sl-ad-campaign-manager-data-model';
import moment from 'moment';

export enum ActionType {
  Budgets = 'budgets',
  Bid = 'bid'
}

// Use this to avoid using 24, which cause the cron schedule to be invalid
export const PreMidnightTime = 23.99

export interface ActionProps {
  method: 'increaseAmount' | 'decreaseAmount' | 'increasePercent' | 'decreasePercent';
  value: number;
  type: 'timeBase';
  name: ActionType;
}

export interface SaveScheduledActionProps {
  scheduleGroupName: string;
  minValueLimit: number | undefined;
  maxValueLimit: number | undefined;
  actionType: ActionType;
  action: ActionProps;
  period: {
    name: PeriodType.DAYS_OF_THE_WEEK;
    startTime: string;
    endTime: string;
    startDate: string;
    endDate: string;
    // Mon - Sun: Need to convert from current Sun - Sat
    weekDaysMapping: boolean[];
  };
  campaigns: any[];
  platformType: string;
  timeZone: string;
}

interface CreateScheduledActionProps {
  platformType: string;
  selectedTimeZone: RuleStoreProps['selectedTimeZone'];
  ruleName: RuleStoreProps['ruleName'];
  rules: RuleStoreProps['rules'];
  days: RuleStoreProps['days'];
  startTime: RuleStoreProps['startTime'];
  endTime: RuleStoreProps['endTime'];
  selectedCampaigns: RuleStoreProps['selectedCampaigns'];
  selectedTargets: RuleStoreProps['selectedTargets'];
  ruleStrategyMetric: RuleStoreProps['rules']['ruleStrategyMetric'];
  ruleTarget: RuleStoreProps['rules']['ruleTarget'];
  adTargetDocuments: any;
  retailerId: string;
  timeZone?: string;
}

export const mapName = (name: string): string => {
  switch (name) {
    case 'Similar product views':
      return 'similar-product';
    default:
      return name?.replace('KEYWORD_GROUP_SAME_AS', 'keyword-group')?.replace('expanded-from', 'asin-expanded');
  }
};

const transformScheduledActionData = ({
  platformType,
  ruleName,
  rules,
  days,
  startTime,
  endTime,
  selectedCampaigns,
  selectedTargets,
  ruleStrategyMetric,
  ruleTarget,
  adTargetDocuments,
  retailerId,
  selectedTimeZone
}: CreateScheduledActionProps) => {
  // Validation
  if (!ruleName || ruleName.trim() === '') {
    throw new Error('Rule name is required.');
  }

  // Validate start and end times
  if (!startTime || !startTime.id) {
    throw new Error('Start time is required.');
  }
  if (!endTime || !endTime.id) {
    throw new Error('End time is required.');
  }

  // Validate days configuration
  if (!days || Object.keys(days).length === 0) {
    throw new Error('Days selection is required.');
  }

  // validate value
  if (!rules.value) {
    throw new Error('Value required for change amount.');
  }

  if (!rules.ruleStrategyUpperBound && rules.ruleStrategy === 'increase') {
    throw new Error('Upper bound is required.');
  }

  if (!rules.ruleStrategyLowerBound && rules.ruleStrategy === 'decrease') {
    throw new Error('Lower bound is required.');
  }

  const ruleStrategyType = ruleStrategyMetric === '%' ? 'Percent' : 'Amount';
  const actionType = ruleTarget === 'adCampaign' ? ActionType.Budgets : ActionType.Bid;
  const weekDays = Object.values(days);
  const timeZone = timeZoneToDateTime(selectedTimeZone, +startTime.id, +endTime.id);

  const period = {
    weekDaysMapping: weekDays.slice(1, 7).concat(weekDays[0]),
    overrideUtc: true,
    ...timeZone
  };

  const selectedData = rules.ruleTarget === 'adCampaign' ? selectedCampaigns : selectedTargets;

  const isInstacart = retailerId === '63';

  const adTargetDocumentsByTargetingText: any = {};

  adTargetDocuments.forEach((adTargetDocument: any) => {
    let targetIdToUse = `${mapName(adTargetDocument.targetingText)}_${adTargetDocument.targetingType}`;

    if (isInstacart) {
      targetIdToUse = adTargetDocument.targetingText + (adTargetDocument.isAutoAddedBoolean ? '1' : '0');
    }

    if (!adTargetDocumentsByTargetingText[targetIdToUse]) {
      adTargetDocumentsByTargetingText[targetIdToUse] = {
        beaconClientId: adTargetDocument.beaconClientId,
        platformType: adTargetDocument.platformType,
        targetingText: mapName(adTargetDocument.targetingText),
        targetingType: adTargetDocument.targetingType,
        extendedAttributes: {
          statusReason: '',
          status: AD_CAMPAIGN_STATUS.PAUSED,
          statusDerived: AD_STATUS_DERIVED.PAUSED,
          childDocuments: []
        }
      };
    }
    if (adTargetDocument.status === AD_CAMPAIGN_STATUS.ENABLED) {
      adTargetDocumentsByTargetingText[targetIdToUse].extendedAttributes.status = AD_CAMPAIGN_STATUS.ENABLED;
    }
    if (adTargetDocument.statusDerived === AD_STATUS_DERIVED.DELIVERING) {
      adTargetDocumentsByTargetingText[targetIdToUse].extendedAttributes.statusDerived = AD_STATUS_DERIVED.DELIVERING;
    }
    if (!['ended', 'archived'].includes(adTargetDocument.statusDerived)) {
      adTargetDocumentsByTargetingText[targetIdToUse].extendedAttributes.childDocuments.push(adTargetDocument);
    }
  });

  const data = Array.from(selectedData.values());

  if (!data || data.length === 0) {
    throw new Error('No campaigns or targets selected.');
  }

  data.forEach((x, index) => {
    const id = `${mapName(x.targetingText)}_${x.targetingType ?? x.extendedAttributes?.matchType}`;

    if (adTargetDocumentsByTargetingText[id]) {
      adTargetDocumentsByTargetingText[id].extendedAttributes.campaignIds = _uniq(
        adTargetDocumentsByTargetingText[id].extendedAttributes.childDocuments.map(
          (adTargetDocument) => adTargetDocument.campaignId
        )
      );
      adTargetDocumentsByTargetingText[id].extendedAttributes.campaignIdsDelivering = _uniq(
        adTargetDocumentsByTargetingText[id].extendedAttributes.childDocuments
          .filter((y) => y.statusDerived === AD_STATUS_DERIVED.DELIVERING)
          .map((adTargetDocument) => adTargetDocument.campaignId)
      );
      adTargetDocumentsByTargetingText[id].extendedAttributes.statusDisplay = getCampaignDisplayStatus(
        adTargetDocumentsByTargetingText[id].extendedAttributes
      );

      data[index] = {
        ...x,
        ...adTargetDocumentsByTargetingText[id],
        extendedAttributes: {
          ...x.extendedAttributes,
          childDocuments: x.extendedAttributes?.childDocuments ?? [],
          ...adTargetDocumentsByTargetingText[id].extendedAttributes
        }
      };
    }
    if (!data[index].extendedAttributes?.childDocuments) {
      console.error('No child documents found for', data[index]);
    }
  });

  const req = {
    scheduleGroupName: ruleName,
    minValueLimit: rules.ruleStrategyLowerBound,
    maxValueLimit: rules.ruleStrategyUpperBound,
    actionType: 'timeBase',
    timeZone: selectedTimeZone,
    action: {
      name: actionType,
      method: `${rules.ruleStrategy}${ruleStrategyType}` as ActionProps['method'],
      value: rules.value,
      type: 'timeBase'
    },
    period,
    campaigns: data,
    platformType,
    overrideUtc: true
  };

  return req;
};

export const createScheduledAction = async ({
  platformType,
  ruleName,
  rules,
  days,
  startTime,
  endTime,
  selectedCampaigns,
  selectedTargets,
  ruleStrategyMetric,
  ruleTarget,
  adTargetDocuments,
  retailerId,
  selectedTimeZone
}: CreateScheduledActionProps) => {
  try {
    const req = transformScheduledActionData({
      platformType,
      ruleName,
      rules,
      days,
      startTime,
      endTime,
      selectedCampaigns,
      selectedTargets,
      ruleStrategyMetric,
      ruleTarget,
      adTargetDocuments,
      retailerId,
      selectedTimeZone
    });

    const res = await saveScheduledActions(req as SaveScheduledActionProps);
    return res;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
};

const BASE_URL = '/apiAdManager/scheduledActions';

export const fetchScheduledAction = async (scheduledActionGroupId: string, platformType: string) => {
  const res = axios.get(`${BASE_URL}/?platformType=${platformType}&scheduledActionGroupId=${scheduledActionGroupId}`);

  return res;
};

const shiftDay = (day: Record<string, boolean>, shift: number) => {
  const values = Object.values(day);
  if (shift > 0) {
    values.unshift(values.pop());
  } else if (shift < 0) {
    values.push(values.shift());
  }
  const keys = Object.keys(day);
  return keys.reduce((acc, key, index) => {
    acc[key] = values[index];
    return acc;
  }, {});
};

export function sortData(data, ruleTarget, entity) {
  return data.sort((a, b) => {
    const keyA = ruleTarget === 'adCampaign' ? a?.campaignId : `${a?.targetingText}_${a?.targetingType}`;
    const keyB = ruleTarget === 'adCampaign' ? b?.campaignId : `${b?.targetingText}_${b?.targetingType}`;

    const checkedA = entity.has(keyA);
    const checkedB = entity.has(keyB);

    if (checkedA === checkedB) {
      return 0;
    }

    return checkedA ? -1 : 1;
  });
}

export const campaignSearchTermFilters = [
  {
    fieldName: 'campaignId',
    queryType: 'wildcard',
    values: []
  },
  {
    fieldName: 'campaignName',
    queryType: 'wildcard',
    values: []
  }
];

export const targetSearchTermFilters = [
  {
    fieldName: 'targetingText',
    queryType: 'wildcard',
    values: []
  }
];

export const parseScheduledActionData = (data: any) => {
  const startData = data[0];
  const endData = data[1];
  const startTime = +moment(startData?.nextExecutionTime).utc().hour();
  const endTime = +moment(endData?.nextExecutionTime).utc().hour();
  const timeZone = startData?.extendedAttributes?.timeZone ?? TimeZone.WesternEuropeanTime;
  const timeZoneOffset = getTimezoneOffset(timeZone);

  const startCron = startData?.cronSchedule.split(/[\s,]+/);

  const startShift = Math.floor((+startCron[2] - timeZoneOffset) / 24);
  const daysData = {
    Sun: false,
    Mon: false,
    Tues: false,
    Wed: false,
    Thu: false,
    Fri: false,
    Sat: false
  };

  const dayMapping = {
    SUN: 'Sun',
    MON: 'Mon',
    TUE: 'Tues',
    WED: 'Wed',
    THU: 'Thu',
    FRI: 'Fri',
    SAT: 'Sat'
  };

  startCron.forEach((day) => {
    if (dayMapping[day]) {
      daysData[dayMapping[day]] = true;
    }
  });

  if (startCron[5] === '?') {
    Object.keys(daysData).forEach((day) => {
      daysData[day] = true;
    });
  }

  const parsedData = shiftDay(daysData, startShift);
  const combinedCampaigns = (
    startData?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adCampaignsBudgetSetting ?? []
  ).concat(
    startData?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adCampaigns ??
    startData?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adCampaignsBudgetSetting ??
    []
  );

  const campaigns = new Map((combinedCampaigns ?? []).map((campaign) => [campaign.campaignId, campaign]));

  let normStartTime = (((startTime + timeZoneOffset) % 24) + 24) % 24;
  let normEndTime = (((endTime + timeZoneOffset) % 24) + 24) % 24;

  if (moment(startData.nextExecutionTime).minute() === 59) {
    normStartTime = PreMidnightTime;
  }

  if (moment(endData.nextExecutionTime).minute() === 59) {
    normEndTime = PreMidnightTime;
  }

  const targets = new Map(
    (startData?.extendedAttributes?.adUpdateRequest?.extendedAttributes?.adTargets ?? []).map((target) => [
      mapName(`${target.targetingText}_${target.extendedAttributes?.matchType}`),
      target
    ])
  );

  const rules = {
    ruleStrategyMetric: startData?.extendedAttributes?.actionPercent !== undefined ? '%' : '$',
    ruleTarget: campaigns.size > 0 ? 'adCampaign' : 'adTarget',
    ruleStrategy:
      startData?.extendedAttributes?.actionMethod === 'Reduce'
        ? 'decrease'
        : startData?.extendedAttributes?.actionMethod.toLowerCase(),
    value: startData?.extendedAttributes?.actionAmount ?? startData?.extendedAttributes?.actionPercent,
    ruleStrategyUpperBound: startData?.extendedAttributes?.maxValueLimit,
    ruleStrategyLowerBound: startData?.extendedAttributes?.minValueLimit
  };

  const ruleStoreData = {
    selectedTimeZone: timeZone,
    startTime: generateDayIdLabel(normStartTime),
    endTime: generateDayIdLabel(normEndTime),
    rules,
    selectedCampaigns: campaigns,
    selectedTargets: targets,
    days: parsedData
  };

  return ruleStoreData;
};

export const editScheduleActionsHelper = async ({
  actionTitle,
  period,
  selectedAction,
  selectedCampaigns,
  minValueLimit,
  maxValueLimit,
  originRequests,
  entityType,
  timeZone
}: {
  actionTitle: any;
  period: any;
  selectedAction: any;
  selectedCampaigns: any;
  minValueLimit: number | undefined;
  maxValueLimit: number | undefined;
  originRequests: any[];
  entityType: string;
  timeZone: string;
}) => {
  const { updateType } = originRequests[0].extendedAttributes.adUpdateRequest;

  const UPDATE_STATUS_SCHEDULED_ACTION_API = `/apiAdManager/scheduledActions/${updateType === 'bulkBudgetUpdate' ? 'updateBulkBudget' : updateType
    }`;
  const changeRateOverwrite = {
    method: selectedAction.method,
    value: selectedAction.value
  };

  let [startCronSchedule, endCronSchedule, startTime, endTime] = ['', '', '', ''];
  endTime = moment(period.endDate).toISOString();
  startTime = moment(period.startDate).toISOString();
  const res = encodeCronSchedule(period);
  startCronSchedule = res.startCron;
  endCronSchedule = res.endCron;

  const handleEntityChange = (req: any, selectedEntities: any[]) => {
    switch (entityType) {
      case 'adTarget': {
        const changeBidRequest = AdCampaignManagerServiceClient.buildUpdateBidRequest({
          beaconClientId: selectedEntities[0].beaconClientId,
          platformType: selectedEntities[0].platformType,
          entities: selectedEntities,
          entityType,
          changeRateOverwrite
        });

        const changedEntities = _cloneDeep(changeBidRequest.extendedAttributes);

        req.extendedAttributes.adUpdateRequest.extendedAttributes = changedEntities;
        break;
      }
      case 'adCampaign':
      case 'adCampaignsBudgetSetting': {
        const changeBudgetRequest = AdCampaignManagerServiceClient.buildBulkBudgetRequest({
          beaconClientId: selectedEntities[0].beaconClientId,
          platformType: selectedEntities[0].platformType,
          entities: selectedEntities,
          entityType,
          changeRateOverwrite
        });

        req.extendedAttributes.adUpdateRequest.extendedAttributes = _cloneDeep(changeBudgetRequest.extendedAttributes);
        break;
      }
      default:
        // all options
        break;
    }
  };
  originRequests[0].extendedAttributes.groupName = actionTitle;
  originRequests[0].extendedAttributes.name = `${actionTitle}-change`;
  originRequests[0].cronSchedule = startCronSchedule;
  originRequests[0].endTime = endTime;
  originRequests[0].startTime = startTime;
  originRequests[0].extendedAttributes.timeZone = timeZone;
  const actionMethod = getActionMethodFromOption(selectedAction.method);
  originRequests[0].extendedAttributes.actionMethod = actionMethod;

  originRequests[0].extendedAttributes.maxValueLimit = maxValueLimit;
  originRequests[0].extendedAttributes.minValueLimit = minValueLimit;

  if (selectedAction && selectedAction.method.toLowerCase().includes('percent')) {
    // percent
    originRequests[0].extendedAttributes.actionPercent = selectedAction.value;
    originRequests[0].extendedAttributes.actionAmount = undefined;
  } else {
    // non persent
    originRequests[0].extendedAttributes.actionPercent = undefined;
    originRequests[0].extendedAttributes.actionAmount = selectedAction.value;
  }

  // Run or Pause
  const [primaryStatus, recoverStatus] =
    selectedAction.method === 'Pause'
      ? [AD_CAMPAIGN_STATUS.PAUSED, AD_CAMPAIGN_STATUS.ENABLED]
      : [AD_CAMPAIGN_STATUS.ENABLED, AD_CAMPAIGN_STATUS.PAUSED];
  originRequests[0].extendedAttributes.statusActionType = primaryStatus;

  handleEntityChange(originRequests[0], selectedCampaigns);

  if (originRequests[1]) {
    // if both presented need to remove one of them
    if (selectedAction && selectedAction.method.toLowerCase().includes('ercent')) {
      originRequests[1].extendedAttributes.actionPercent = selectedAction.value;
      originRequests[1].extendedAttributes.actionAmount = undefined;
    } else {
      // non persent
      originRequests[1].extendedAttributes.actionPercent = undefined;
      originRequests[1].extendedAttributes.actionAmount = selectedAction.value;
    }
    originRequests[1].extendedAttributes.statusActionType = recoverStatus;
    originRequests[1].extendedAttributes.groupName = actionTitle;
    originRequests[1].extendedAttributes.name = `${actionTitle}-recover`;

    // inverting the action method
    originRequests[1].extendedAttributes.actionMethod =
      getActionMethodFromOption(selectedAction.method) === ActionMethod.Increase
        ? ActionMethod.Reduce
        : ActionMethod.Increase;
    originRequests[1].cronSchedule = endCronSchedule;
    originRequests[1].endTime = endTime;
    originRequests[1].startTime = startTime;
    originRequests[1].extendedAttributes.minValueLimit = minValueLimit;
    originRequests[1].extendedAttributes.maxValueLimit = maxValueLimit;
    originRequests[1].extendedAttributes.timeZone = timeZone;
    handleEntityChange(originRequests[1], selectedCampaigns);
  }

  return axios.post(UPDATE_STATUS_SCHEDULED_ACTION_API, originRequests);
};

export const editScheduledAction = async ({
  platformType,
  ruleName,
  rules,
  days,
  startTime,
  endTime,
  selectedCampaigns,
  selectedTargets,
  ruleStrategyMetric,
  ruleTarget,
  adTargetDocuments,
  retailerId,
  selectedTimeZone,
  originRequests
}) => {
  try {
    const req = transformScheduledActionData({
      platformType,
      ruleName,
      rules,
      days,
      startTime,
      endTime,
      selectedCampaigns,
      selectedTargets,
      ruleStrategyMetric,
      ruleTarget,
      adTargetDocuments,
      retailerId,
      selectedTimeZone
    });

    const res = await editScheduleActionsHelper({
      actionTitle: req.scheduleGroupName,
      period: req.period,
      selectedAction: req.action,
      selectedCampaigns: req.campaigns,
      minValueLimit: req.minValueLimit,
      maxValueLimit: req.maxValueLimit,
      entityType: ruleTarget,
      timeZone: req.timeZone,
      originRequests
    });

    return res;
  } catch (err) {
    console.error(err);
    return Promise.reject(err);
  }
};
